import React, { useEffect, useMemo, useRef, useState } from 'react'
import { TextField, Grid, InputAdornment, Chip, Box, Paper, Typography, Tooltip, Stack } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { EditRounded, InfoOutlined } from '@mui/icons-material'
import { DefaultAccordion } from '../../generic/DefaultAccordion'
import ButtonBackSave from '../../generic/buttons/floatingButtons/ButtonBackSave'
import { apiGet, apiPut } from '../../generic/Api_Functions'
import { useNavigate } from 'react-router-dom'
import AlertUI from '../../generic/AlertUI'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { CheckCorrectTimeFormat, durationFromMsToString, durationStringToMilliseconds, durationFromMsToMinutes, durationFromMinutesToMs } from '../../generic/DateTimeFunctions'
import SmartSelect from '../../generic/smartSelect/SmartSelect'

// Story 213 (C11.4, DA-6, GATE 2 bis C12.4): the VISION section is edited in BACKOFFICE › Camera control, only by the
// SUPERADMIN. Settings neither paints nor saves its rows, nor offers any access to it (S115).
const VISION_SECTION = 'VISION'

// Import typedefs from another file
/**
 * @typedef {import('../../generic/utilities/objectStructures/settings/settings').ConfigurationSection} ConfigurationSection ConfigurationSection object
 */

const Settings = (props) => {
  const backSaveButtonRef = useRef()
  const { t_ } = useSmartTranslation()
  const [editMode, setEditMode] = useState(false)
  const { reloadTopBar, setReloadTopBar } = props
  /** @type {[ConfigurationSection[], React.Dispatch<SetStateAction<ConfigurationSection[]>>]} */
  const [settings, setSettings] = useState([])
  const [AlertElement, showAlert] = AlertUI()
  const changeBetweenVisualizeModes = (edit) => {
    setEditMode(edit)
  }
  const navigate = useNavigate()
  const [errors, setErrors] = useState({})
  const [oeeAdjustment, setOeeAdjustment] = useState('0.00')

  const saveChanges = async () => {
    try {
      const sendData = []
      let error = null
      settings.filter((item) => item.name !== VISION_SECTION).forEach((item) => {
        item.configurations.forEach((conf) => {
          conf = { ...conf }
          if (conf.type === 'Duration') {
            try {
              conf.value = durationStringToMilliseconds(conf.value, t_)
            } catch (durationError) {
              error = durationError
            }
          }
          if (conf.type === 'Number' && conf.key.startsWith('OEE_')) {
            try {
              conf.value = checkOEE(conf.value)
            } catch (oeeError) {
              error = oeeError
            }
          }
          if (conf.type === 'Number' && conf.key === 'READ_TOOL_TABLE_INTERVAL') {
            try {
              conf.value = durationFromMinutesToMs(conf.value)
            } catch (durationError) {
              error = durationError
            }
          }
          // Normalize CO2_PER_KWH_*: empty/invalid/negative -> 'empty'; valid -> number >=0 with 2 decimals
          if (conf.type === 'Number' && conf.key.startsWith('CO2_PER_KWH_')) {
            const raw = conf.value?.toString().trim()
            if (!raw) {
              conf.value = 'empty'
            } else {
              const parsed = parseFloat(raw.replace(',', '.'))
              if (isNaN(parsed) || parsed < 0) {
                conf.value = 'empty'
              } else {
                conf.value = parseFloat(parsed.toFixed(2))
              }
            }
          }
          // Prepare Durations
          sendData.push(conf)
        })
      })
      if (error) {
        showAlert({ severity: 'error', title: ('Error saving'), message: error.message ? error.message : '' })
      } else {
        await apiPut('settings', sendData)
        showAlert({ severity: 'success', title: t_('Saved correctly'), message: '  ' })
        window.location.reload()
        setReloadTopBar(!reloadTopBar)
      }
    } catch (error) {
      // Sun rejects with a literal that is also the i18n key (S69, e.g. FeatureGroupChangeControlRequiresWorkflowIndex)
      showAlert({ severity: 'error', title: ('Error saving'), message: error?.message ? t_(error.message) : error })
    }
  }

  useEffect(() => {
    const fetchFunction = async () => {
      try {
        const fetchedData = await apiGet('settings')
        fetchedData.forEach((section) => {
          section.configurations.forEach((conf) => {
            if (conf.type === 'Duration') {
              conf.value = durationFromMsToString(conf.value)
            }
            if (conf.type === 'Number' && conf.key === 'READ_TOOL_TABLE_INTERVAL') {
              conf.value = durationFromMsToMinutes(conf.value)
            }
            // For CO2_PER_KWH_* if backend returns 'empty', show empty in UI
            if (conf.type === 'Number' && conf.key.startsWith('CO2_PER_KWH_')) {
              if (conf.value === 'empty') {
                conf.value = ''
              }
            }
          })
        })

        setSettings(fetchedData)
      } catch (error) {
        console.error('Error fetching data:', error)
      }
    }
    fetchFunction()
  }, [])

  const plannerType = useMemo(() => {
    if (!settings) {
      return null
    }
    // Check planner type
    const plannerSection = settings.find((section) => section.name === 'PLANNING')
    if (!plannerSection) {
      return null
    }
    const plannerType = plannerSection.configurations.find((conf) => conf.key === 'PLANNER_TYPE')
    if (!plannerType) {
      return null
    }
    return plannerType.value
  }, [settings])

  // Check configuration is valid. Change values if required
  useEffect(() => {
    if (!plannerType || !settings) {
      return
    }
    // Force PLANNER_LOAD for AIPLANNER/SEQUENCER. Guarded so it only updates
    // when the value actually changes, otherwise setSettings([...settings])
    // would build a new array on every render and loop indefinitely.
    if (plannerType === 'AIPLANNER') {
      const plannerSection = settings.find((section) => section.name === 'PLANNING')
      const loadConfiguration = plannerSection.configurations.find((conf) => conf.key === 'PLANNER_LOAD')
      if (loadConfiguration && loadConfiguration.value !== 'FINITE') {
        loadConfiguration.value = 'FINITE'
        setSettings([...settings])
      }
    }
    if (plannerType === 'SEQUENCER') {
      const plannerSection = settings.find((section) => section.name === 'PLANNING')
      const loadConfiguration = plannerSection.configurations.find((conf) => conf.key === 'PLANNER_LOAD')
      if (loadConfiguration && loadConfiguration.value !== 'INFINITE') {
        loadConfiguration.value = 'INFINITE'
        setSettings([...settings])
      }
    }
  }, [settings, plannerType])

  const goBack = () => {
    navigate(-1)
  }
  const configurationCondition = (section, configuration) => {
    if (configuration.key === 'PLANNER_LOAD') {
      return plannerType === 'PLANNER'
    }
    if (configuration.key === 'PLANNER_DEFAULT_TRANSPORT_TIME') {
      return plannerType === 'AIPLANNER'
    }
    if (section.name === 'PLANNING') {
      return configuration.key === 'PLANNER_TYPE' || plannerType !== 'SEQUENCER'
    }
    return true
  }

  // PLAN_CELLS_ONLY (cells + warehouse capacity) does not apply to the
  // sequencer planner, so it is hidden entirely when PLANNER_TYPE is SEQUENCER.
  const configurationHidden = (configuration) => {
    if (configuration.key === 'PLAN_CELLS_ONLY') {
      return plannerType === 'SEQUENCER'
    }
    return false
  }

  const checkOEE = (newValue) => {
    let numericValue = parseFloat(0)
    if (newValue && !isNaN(newValue)) {
      numericValue = parseFloat(newValue)
      if (!(/^\d+(\.\d{1,2})?$/.test(newValue))) {
        numericValue = Math.round(numericValue * 100) / 100
      }

      if (numericValue < 0) {
        throw new Error(t_('The minimum value is 0%'))
      } else if (numericValue > 100) {
        throw new Error(t_('The maximum value is 100%'))
      }
      return numericValue
    }
    return numericValue
  }

  const handleOnChange = (event, configuration, settingIndex, configurationIndex) => {
    let newValue
    if (configuration.type === 'List') {
      newValue = event
    } else if (configuration.type === 'Boolean') {
      newValue = event ? 'true' : 'false'
    } else {
      newValue = event.target.value.toString()
    }

    if (configuration.type === 'Number' && configuration.key.startsWith('OEE_')) {
      if (newValue.startsWith('0') && newValue.length > 1) {
        newValue = newValue.replace(/^0+/, '')
      }

      if (newValue.length > 3) {
        newValue = newValue.slice(0, 3)
      }

      if (parseInt(newValue, 10) > 100) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          [configuration.key]: t_('The maximum value is 100%')
        }))
      } else {
        setErrors((prevErrors) => {
          const updatedErrors = { ...prevErrors }
          delete updatedErrors[configuration.key]
          return updatedErrors
        })
      }
    }

    const newSettings = [...settings]
    newSettings[settingIndex].configurations[configurationIndex].value = newValue
    setSettings(newSettings)
  }

  // On blur for CO2_PER_KWH_*: show empty if invalid/empty; if valid, clamp >=0 and 2 decimals
  const handleOnBlur = (event, configuration, settingIndex, configurationIndex) => {
    if (configuration.type === 'Number' && configuration.key.startsWith('CO2_PER_KWH_')) {
      const raw = event.target.value?.toString().trim() ?? ''
      let formatted = ''
      if (raw) {
        const parsed = parseFloat(raw.replace(',', '.'))
        formatted = (isNaN(parsed) || parsed < 0) ? '' : parsed.toFixed(2)
      }

      const newSettings = [...settings]
      newSettings[settingIndex].configurations[configurationIndex].value = formatted
      setSettings(newSettings)
    }
  }

  useEffect(() => {
    recalculateOEE(settings)
  }, [settings])

  const recalculateOEE = (updatedSettings) => {
    let availability = 0; let performance = 0; let quality = 0

    updatedSettings.forEach((setting) => {
      setting.configurations.forEach((config) => {
        if (config.key === 'OEE_AVAILABILITY') {
          availability = parseFloat(config.value) / 100 || 0
        }
        if (config.key === 'OEE_PERFORMANCE') {
          performance = parseFloat(config.value) / 100 || 0
        }
        if (config.key === 'OEE_QUALITY') {
          quality = parseFloat(config.value) / 100 || 0
        }
      })
    })

    const ajusteOEE = 1 - ((1 - availability) * (1 - performance) * (1 - quality))
    setOeeAdjustment(ajusteOEE.toFixed(2))
  }

  // Help text and option labels of a setting are opt-in, by convention of i18n keys derived from its key and its
  // stored values (story 212, contract C3): a setting that declares none renders exactly as before. A key "exists"
  // when i18next resolves it to something else than the key itself (the "en" fallback included).
  const translationExists = (key) => t_(key) !== key
  const helpKeyOf = (configuration) => `settingHelp_${configuration.key}`
  const optionKeyOf = (configuration, option) => `settingOption_${configuration.key}_${option}`

  // Translated options keep their stored value and Sun's order: sorting the raw values would not follow the labels.
  const translatedOptionsProps = (configuration) => {
    const hasTranslatedOptions = configuration.values.some(value => translationExists(optionKeyOf(configuration, value.value)))
    if (!hasTranslatedOptions) {
      return {}
    }
    return {
      optionLabelTransform: (option) => translationExists(optionKeyOf(configuration, option)) ? t_(optionKeyOf(configuration, option)) : option,
      sortValues: false
    }
  }

  // Renders a single configuration field. All variants (select, boolean, text/number)
  // share the same outlined look and height so the grid stays uniform.
  const renderField = (setting, configuration, settingIndex, configurationIndex) => {
    const errorMsg = configuration.type === 'Duration' ? CheckCorrectTimeFormat(configuration.value, t_) : false
    const disabled = !editMode || !configurationCondition(setting, configuration)

    if (configuration.type === 'List') {
      return (
        <SmartSelect
          label={t_(configuration.name)}
          value={configuration.value}
          selectableOptions={configuration.values.map(value => (value.value)) || []}
          onChange={(newValue) => handleOnChange(newValue, configuration, settingIndex, configurationIndex)}
          disabled={disabled || configuration.values.length <= 1}
          {...translatedOptionsProps(configuration)}
        />
      )
    }

    if (configuration.type === 'Boolean') {
      const checked = configuration.value === 'true'
      return (
        <SmartSelect
          label={t_(configuration.name)}
          value={checked ? t_('YES') : t_('NO')}
          selectableOptions={[t_('YES'), t_('NO')]}
          onChange={(selected) => handleOnChange(selected === t_('YES'), configuration, settingIndex, configurationIndex)}
          disabled={disabled}
        />
      )
    }

    return (
      <TextField
        type={configuration.type === 'Number' ? 'number' : 'text'}
        fullWidth
        label={t_(configuration.name)}
        value={configuration.key.startsWith('CO2_PER_KWH_') && (configuration.value === 'empty' || configuration.value === null || configuration.value === undefined)
          ? ''
          : configuration.value}
        onChange={(event) => handleOnChange(event, configuration, settingIndex, configurationIndex)}
        onBlur={(event) => handleOnBlur(event, configuration, settingIndex, configurationIndex)}
        disabled={disabled}
        InputProps={{
          readOnly: !editMode,
          endAdornment: configuration.type === 'Number'
            ? configuration.key.startsWith('OEE_')
              ? <InputAdornment position='end'>%</InputAdornment>
              : configuration.key === 'MAX_FILE_SIZE'
                ? <InputAdornment position='end'>Mb</InputAdornment>
                : configuration.key === 'READ_TOOL_TABLE_INTERVAL'
                  ? <InputAdornment position='end'>{t_('Minutes')}</InputAdornment>
                  : configuration.key.startsWith('CO2_PER_KWH_')
                    ? <InputAdornment position='end'>kg CO₂e/kWh</InputAdornment>
                    : null
            : null
        }}
        inputProps={{
          min: configuration.type === 'Number' && configuration.key.startsWith('OEE_')
            ? 0
            : configuration.type === 'Number' && configuration.key.startsWith('READ_TOOL_TABLE_INTERVAL')
              ? 1
              : configuration.type === 'Number' && configuration.key.startsWith('CO2_PER_KWH_')
                ? 0
                : null,
          max: configuration.type === 'Number' && configuration.key.startsWith('OEE_') ? 100 : null,
          step: configuration.type === 'Number' && configuration.key.startsWith('CO2_PER_KWH_') ? '0.01' : undefined,
          inputMode: configuration.type === 'Number' && configuration.key.startsWith('CO2_PER_KWH_') ? 'decimal' : undefined
        }}
        error={!!errors[configuration.key]}
        helperText={errors[configuration.key] || (editMode ? errorMsg : '')}
      />
    )
  }

  // The field, with its help icon next to it only when the setting declares a help text (same look as the OEE index).
  const renderFieldWithHelp = (setting, configuration, settingIndex, configurationIndex) => {
    const field = renderField(setting, configuration, settingIndex, configurationIndex)
    if (!translationExists(helpKeyOf(configuration))) {
      return field
    }
    return (
      <Stack direction='row' alignItems='center' spacing={0.5}>
        <Box sx={{ flex: 1, minWidth: 0 }}>{field}</Box>
        <Tooltip title={t_(helpKeyOf(configuration))} placement='top'>
          <InfoOutlined data-testid={'setting-help-' + configuration.key} sx={{ fontSize: 18, color: 'text.secondary' }} />
        </Tooltip>
      </Stack>
    )
  }

  const renderedElements = settings.map((setting, settingIndex) => {
    const hasOEEValues = setting.configurations.some(config =>
      ['OEE_AVAILABILITY', 'OEE_PERFORMANCE', 'OEE_QUALITY'].includes(config.key)
    )

    if (setting.name === VISION_SECTION) {
      return null
    }

    return (
      <DefaultAccordion title={t_(setting.name)} key={settingIndex}>
        <Grid container direction='row' spacing={3} columns={{ xs: 1, sm: 4, md: 8, lg: 12 }} alignItems='flex-start'>
          {setting.configurations.map((configuration, configurationIndex) => (
            configurationHidden(configuration)
              ? null
              : (
                <Grid key={'Grid' + configuration.key + configurationIndex} item xs={configuration.key === 'Language' ? 1.5 : 3}>
                  {renderFieldWithHelp(setting, configuration, settingIndex, configurationIndex)}
                </Grid>
                )
          ))}

          {hasOEEValues && (
            <Grid key='OEE_Adjustment' item xs={3}>
              <Paper
                variant='outlined'
                sx={{
                  px: 2,
                  height: theme => theme.spacing(7),
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  borderRadius: 1.5,
                  borderColor: theme => alpha(theme.palette.primary.main, 0.5),
                  backgroundColor: theme => alpha(theme.palette.primary.main, 0.06)
                }}
              >
                <Stack direction='row' alignItems='center' spacing={0.5}>
                  <Typography variant='caption' color='text.secondary' sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {t_('OEE adjustment index')}
                  </Typography>
                  <Tooltip title={t_('Calculated as 1 - (1 - availability) x (1 - performance) x (1 - quality)')} placement='top'>
                    <InfoOutlined sx={{ fontSize: 14, color: 'text.secondary' }} />
                  </Tooltip>
                </Stack>
                <Typography variant='h6' color='primary' sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                  {oeeAdjustment}
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      </DefaultAccordion>
    )
  })

  return (
    <>
      {AlertElement}
      {editMode && (
        <Box sx={{ position: 'sticky', top: 0, zIndex: 3, display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Chip color='primary' size='small' icon={<EditRounded />} label={t_('Edit mode')} />
        </Box>
      )}
      {renderedElements}
      <ButtonBackSave
        editControl={() => changeBetweenVisualizeModes(!editMode)}
        backButtonAction={() => goBack('BACK')}
        saveButtonAction={() => { saveChanges() }}
        ref={backSaveButtonRef}
        useOnSaveClick
      />
    </>
  )
}
export default Settings
